import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Divider,
  Box,
  Typography,
  Chip,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Inventory as InventoryIcon,
  Category as CategoryIcon,
  People as PeopleIcon,
  PointOfSale as PointOfSaleIcon,
  Receipt as ReceiptIcon,
  Assessment as AssessmentIcon,
  Inventory2 as StockIcon,
  Person as PersonIcon,
  AssignmentReturn as DevolucaoIcon,
  LocalOffer as CupomIcon,
  CreditCard as CreditoIcon,
  Subscriptions as SubscriptionIcon,
  Storefront as StorefrontIcon,
  Event as EventIcon,
  Restaurant as RestaurantIcon,
  AccountBalanceWallet as CaixaIcon,
} from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { useTenant } from '../../../contexts/TenantContext';
import { PATHS } from '../../../routes/paths';

interface SidebarProps {
  open: boolean;
  onClose: () => void;
  drawerWidth: number;
}

interface MenuItem {
  label: string;
  path: string;
  icon: React.ReactNode;
  roles?: string[];
  feature?: string; // Feature requerida do plano
}

const menuItems: MenuItem[] = [
  { label: 'Dashboard', path: PATHS.DASHBOARD, icon: <DashboardIcon /> },
  { label: 'PDV', path: PATHS.PDV, icon: <PointOfSaleIcon /> },
  { label: 'Eventos', path: PATHS.EVENTOS, icon: <EventIcon />, roles: ['ADMIN', 'GERENTE'] },
  { label: 'Pedidos', path: PATHS.PEDIDOS, icon: <RestaurantIcon /> },
  { label: 'Caixa', path: PATHS.CAIXA, icon: <CaixaIcon /> },
  { label: 'Vendas', path: PATHS.VENDAS, icon: <ReceiptIcon /> },
  { label: 'Produtos', path: PATHS.PRODUTOS, icon: <InventoryIcon /> },
  { label: 'Categorias', path: PATHS.CATEGORIAS, icon: <CategoryIcon /> },
  { label: 'Clientes', path: PATHS.CLIENTES, icon: <PeopleIcon /> },
  { label: 'Estoque', path: PATHS.ESTOQUE, icon: <StockIcon />, roles: ['ADMIN', 'GERENTE'] },
  { label: 'Devolucoes', path: PATHS.DEVOLUCOES, icon: <DevolucaoIcon />, roles: ['ADMIN', 'GERENTE'], feature: 'DEVOLUCOES' },
  { label: 'Cupons', path: PATHS.CUPONS, icon: <CupomIcon />, roles: ['ADMIN', 'GERENTE'], feature: 'CUPONS' },
  { label: 'Credito', path: PATHS.CREDITO, icon: <CreditoIcon />, roles: ['ADMIN', 'GERENTE'], feature: 'CREDITO' },
  { label: 'Relatorios', path: PATHS.RELATORIOS, icon: <AssessmentIcon />, roles: ['ADMIN', 'GERENTE'], feature: 'RELATORIOS' },
  { label: 'Usuarios', path: PATHS.USUARIOS, icon: <PersonIcon />, roles: ['ADMIN'], feature: 'MULTI_USUARIOS' },
  { label: 'Assinatura', path: PATHS.SUBSCRIPTION, icon: <SubscriptionIcon />, roles: ['ADMIN'] },
];

export function Sidebar({ open, onClose, drawerWidth }: SidebarProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const { hasFeature } = useTenant();

  const handleNavigation = (path: string) => {
    navigate(path);
    onClose();
  };

  const filteredMenuItems = menuItems.filter((item) => {
    // Verifica permissao de role
    const hasRole = !item.roles || hasPermission(item.roles);
    // Verifica feature do plano
    const hasPlanFeature = !item.feature || hasFeature(item.feature);
    return hasRole && hasPlanFeature;
  });

  const drawerContent = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Toolbar sx={{ px: 2.5, minHeight: '72px !important' }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            cursor: 'pointer',
          }}
          onClick={() => navigate(PATHS.DASHBOARD)}
        >
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 2,
              background: '#2563eb',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 4px 12px rgba(37, 99, 235, 0.25)',
            }}
          >
            <StorefrontIcon sx={{ fontSize: 22, color: 'white' }} />
          </Box>
          <Typography
            variant="h6"
            fontWeight={800}
            sx={{
              color: '#ffffff',
              letterSpacing: '0.06em',
            }}
          >
            DAEX
          </Typography>
        </Box>
      </Toolbar>
      <Divider />
      <Box sx={{ px: 2.5, pt: 2.5, pb: 0.75 }}>
        <Typography variant="caption" sx={{ color: '#94a3b8', fontWeight: 700, letterSpacing: '0.10em' }}>
          GESTÃO COMERCIAL
        </Typography>
      </Box>
      <List sx={{ flex: 1, py: 0.5 }}>
        {filteredMenuItems.map((item) => {
          const isSelected = location.pathname === item.path;
          return (
            <ListItem key={item.path} disablePadding sx={{ px: 1 }}>
              <ListItemButton
                selected={isSelected}
                onClick={() => handleNavigation(item.path)}
                sx={{
                  borderRadius: 1.5,
                  mb: 0.5,
                  '&.Mui-selected': {
                    bgcolor: 'rgba(37, 99, 235, 0.22)',
                    '&:hover': {
                      bgcolor: 'rgba(37, 99, 235, 0.30)',
                    },
                    '& .MuiListItemIcon-root': {
                      color: '#93c5fd',
                    },
                    '& .MuiListItemText-primary': {
                      color: '#ffffff',
                      fontWeight: 600,
                    },
                  },
                  '&:hover': {
                    bgcolor: 'rgba(255, 255, 255, 0.07)',
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 40,
                    color: isSelected ? '#93c5fd' : '#94a3b8',
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                <ListItemText
                  primary={item.label}
                  primaryTypographyProps={{
                    sx: {
                      color: isSelected ? '#ffffff' : '#cbd5e1',
                      fontWeight: isSelected ? 600 : 400,
                    },
                  }}
                />
                {item.path === PATHS.PDV && (
                  <Chip
                    label="Ativo"
                    size="small"
                    sx={{
                      height: 20,
                      fontSize: '0.65rem',
                      bgcolor: 'rgba(96, 165, 250, 0.18)',
                      color: '#bfdbfe',
                      fontWeight: 600,
                    }}
                  />
                )}
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>
      <Divider />
      <Box sx={{ p: 2 }}>
        <Box
          sx={{
            p: 2,
            borderRadius: 2,
            bgcolor: 'rgba(255, 255, 255, 0.05)',
            border: '1px solid rgba(255, 255, 255, 0.08)',
          }}
        >
          <Typography variant="caption" sx={{ color: '#94a3b8', display: 'block', fontWeight: 600 }}>
            DAEX Gestão
          </Typography>
          <Typography variant="caption" sx={{ color: '#64748b' }}>
            Versão 1.0.0
          </Typography>
        </Box>
      </Box>
    </Box>
  );

  return (
    <Box
      component="nav"
      sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
    >
      {/* Mobile drawer */}
      <Drawer
        variant="temporary"
        open={open}
        onClose={onClose}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', sm: 'none' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: drawerWidth,
            bgcolor: '#0f172a',
            borderRight: '1px solid #1e293b',
          },
        }}
      >
        {drawerContent}
      </Drawer>

      {/* Desktop drawer */}
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: 'none', sm: 'block' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: drawerWidth,
            bgcolor: '#0f172a',
            borderRight: '1px solid #1e293b',
          },
        }}
        open
      >
        {drawerContent}
      </Drawer>
    </Box>
  );
}

export default Sidebar;
